import { useTranslation } from "react-i18next";
import {
  Button,
  Divider,
  Grid,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Slider,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { State } from "modules/shop_grid/types";
import { Api } from "queries/types";
import { useState } from "react";
import { chunk, cloneDeep, maxBy } from "lodash";
import { buildSections } from "modules/shop_grid/buildGridState";
import LegendItem from "components/LegendItem";
import GridCell from "components/GridCell";

interface SectionInventoryProps {
  index: number;
  inventory: State.Inventory;
  inventoryCount: number;
  families: Api.Family[];
  removeInventory: () => void;
  updateQuantity: (quantity: number) => void;
  selectFamily: (family: State.Family) => void;
  selectSubFamily: (subFamily: State.SubFamily) => void;
}

interface CellInventoryFormProps {
  cells: State.Cell[];
  families: Api.Family[];
  onSubmit: (sections: State.Section[]) => void;
  unselectAll: () => void;
}

const valueLabelFormat = (value: number) => SLIDER_MARKS.find((mark) => mark.value === value)!?.label;

const autoQuantities = (inventories: State.Inventory[]) => {
  const quantities = [0, 100, 50, 33, 25];
  const inventoryCount = inventories.length;

  inventories.forEach((inventory) => {
    inventory.quantity = quantities[inventoryCount];
  });
};

const CellInventoryForm = ({ cells, families, onSubmit, unselectAll }: CellInventoryFormProps) => {
  const { t } = useTranslation();
  const [sections, setSections] = useState(() =>
    cells.length === 1
      ? cloneDeep(cells[0].sections)
      : buildSections({ shelvesCount: maxBy(cells, "shelvesCount")!.shelvesCount })
  );

  const addInventory = (sectionIndex: number) => {
    const newSections = cloneDeep(sections);
    newSections[sectionIndex].inventories.push({
      quantity: 0,
      family: null,
      subFamily: null,
    });
    autoQuantities(newSections[sectionIndex].inventories);
    setSections(newSections);
  };

  const updateQuantity = (sectionIndex: number, inventoryIndex: number, quantity: number) => {
    const newSections = cloneDeep(sections);
    newSections[sectionIndex].inventories[inventoryIndex].quantity = quantity;

    const quantityLeft = 100 - quantity;
    const otherInventory = newSections[sectionIndex].inventories.find((inventory, index) => index !== inventoryIndex);
    otherInventory!.quantity = quantityLeft;
    setSections(newSections);
  };

  const removeInventory = (sectionIndex: number, inventoryIndex: number) => {
    const newSections = cloneDeep(sections);
    newSections[sectionIndex].inventories = newSections[sectionIndex].inventories.filter(
      (inventory, index) => index !== inventoryIndex
    );
    autoQuantities(newSections[sectionIndex].inventories);
    setSections(newSections);
  };

  const selectFamily = (sectionIndex: number, inventoryIndex: number, family: State.Family) => {
    const newSections = cloneDeep(sections);
    newSections[sectionIndex].inventories[inventoryIndex].family = family;
    newSections[sectionIndex].inventories[inventoryIndex].subFamily = null;
    setSections(newSections);
  };

  const selectSubFamily = (sectionIndex: number, inventoryIndex: number, subFamily: State.SubFamily) => {
    const newSections = cloneDeep(sections);
    newSections[sectionIndex].inventories[inventoryIndex].subFamily = subFamily;
    setSections(newSections);
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(sections);
      }}
    >
      <Grid container direction="column" mt={2}>
        <Grid container direction="row" py={2}>
          <Grid item xs={12}>
            <Typography>
              {t("selectedCells", { count: cells.length })}{" "}
              <Button size="small" color="warning" onClick={unselectAll}>
                {t("unselect")}
              </Button>
            </Typography>
          </Grid>
          {chunk(cells, 3).map((chunk, i) => (
            <Grid item key={i}>
              {chunk.map((cell) => (
                <LegendItem key={`${cell.yAxis}-${cell.xAxis}`} text={`(${cell.yAxis};${cell.xAxis})`} sx={{ mr: 1 }}>
                  <GridCell mode="inventory" cell={cell} />
                </LegendItem>
              ))}
            </Grid>
          ))}
        </Grid>
        {sections.map((section, sectionIndex) => (
          <CellSection
            key={sectionIndex}
            section={section}
            index={sectionIndex}
            families={families}
            addInventory={() => addInventory(sectionIndex)}
            updateQuantity={(inventoryIndex, quantity) => updateQuantity(sectionIndex, inventoryIndex, quantity)}
            removeInventory={(inventoryIndex) => removeInventory(sectionIndex, inventoryIndex)}
            selectFamily={(inventoryIndex, id) => selectFamily(sectionIndex, inventoryIndex, id)}
            selectSubFamily={(inventoryIndex, id) => selectSubFamily(sectionIndex, inventoryIndex, id)}
          />
        ))}
        <Button
          variant="contained"
          sx={{ alignSelf: "flex-end", mt: 2, width: { xs: "100%", sm: "auto" } }}
          type="submit"
          size="large"
        >
          {t("validate")}
        </Button>
      </Grid>
    </form>
  );
};

interface CellSectionProps {
  index: number;
  section: State.Section;
  families: Api.Family[];
  addInventory: () => void;
  updateQuantity: (inventoryIndex: number, quantity: number) => void;
  removeInventory: (inventoryIndex: number) => void;
  selectFamily: (inventoryIndex: number, family: State.Family) => void;
  selectSubFamily: (inventoryIndex: number, subFamily: State.SubFamily) => void;
}

const CellSection = ({
  index,
  section,
  families,
  addInventory,
  updateQuantity,
  removeInventory,
  selectFamily,
  selectSubFamily,
}: CellSectionProps) => {
  const { t } = useTranslation();

  return (
    <Grid item xs={12} mt={2}>
      <Grid container direction="column">
        <Grid item xs={12}>
          <Typography variant="h6">{t("shop.inventory.shelf", { count: index + 1 })}</Typography>
        </Grid>
        {section.inventories.map((inventory, index) => (
          <Grid item xs={12} key={index}>
            <SectionInventory
              inventory={inventory}
              index={index}
              families={families}
              updateQuantity={(quantity) => updateQuantity(index, quantity)}
              removeInventory={() => removeInventory(index)}
              selectFamily={(family) => selectFamily(index, family)}
              selectSubFamily={(subFamily) => selectSubFamily(index, subFamily)}
              inventoryCount={section.inventories.length}
            />
          </Grid>
        ))}
        {section.inventories.length < 4 && (
          <Grid item xs={12} mt={3}>
            <Button
              variant="contained"
              color="info"
              size="small"
              onClick={addInventory}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {t("shop.inventory.addZoning")}
            </Button>
          </Grid>
        )}
      </Grid>
      <Divider sx={{ mt: 2 }} />
    </Grid>
  );
};

const SLIDER_MARKS = [
  { value: 33, label: "1/3" },
  { value: 50, label: "1/2" },
  { value: 67, label: "2/3" },
  { value: 25, label: "1/4" },
  { value: 75, label: "3/4" },
];

const SectionInventory = ({
  index,
  inventory,
  inventoryCount,
  families,
  removeInventory,
  updateQuantity,
  selectFamily,
  selectSubFamily,
}: SectionInventoryProps) => {
  const { t } = useTranslation();
  const subFamilies = inventory.family?.id
    ? families.find((family) => family.id === inventory.family!.id)?.subFamilies || []
    : [];

  return (
    <Grid container mt={4}>
      <Grid item xs={1}>
        <IconButton onClick={removeInventory} color="primary" aria-label={t("a11y.deleteInventory")}>
          <DeleteIcon sx={{ mx: "auto" }} />
        </IconButton>
      </Grid>
      <Grid item xs={11} sm={3} sx={{ mr: { sm: 4 }, pl: { xs: 2 } }}>
        <FormControl fullWidth>
          <InputLabel
              sx={{fontSize: "1.15rem", color: "black", fontWeight: "Medium"  }}
          >
            {t("shop.inventory.family", { index: index + 1 })}
          </InputLabel>
          <Select
            required
            label={t("shop.inventory.family", { index: index + 1 })}
            onChange={(e: any) => selectFamily(families.find((family) => family.id === e.target.value)!)}
            value={inventory.family?.id || ""}
            sx={{ '.MuiInputBase-input': { fontSize: "1.15rem", color: "black", fontWeight: "Medium" }, }}
          >
            {families.map((family) => (
              <MenuItem
                  key={family.id}
                  value={family.id}
                  sx={{ fontSize: "1.15rem", color: "black", fontWeight: "Medium" }}
              >
                {family.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={3} sx={{ mr: { sm: 6 }, mt: { xs: 2, sm: 0 } }}>
        <FormControl fullWidth>
          <InputLabel             
                sx={{fontSize: "1.15rem", color: "black", fontWeight: "Medium"  }}
                  >
              {t("shop.inventory.subFamily", { index: index + 1 })}</InputLabel>
          <Select
            required
            label={t("shop.inventory.subFamily", { index: index + 1 })}
            disabled={subFamilies.length === 0}
            onChange={(e: any) => selectSubFamily(subFamilies.find((subFamily) => subFamily.id === e.target.value)!)}
            value={inventory.subFamily?.id || ""}
            sx={{ '.MuiInputBase-input': { fontSize: "1.15rem", color: "black", fontWeight: "Medium" }, }}
          >
            {subFamilies.map((subFamily) => (
              <MenuItem
                  key={subFamily.id}
                  value={subFamily.id}
                  sx={{ fontSize: "1.15rem", color: "black", fontWeight: "Medium" }}
              >
                {subFamily.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      {inventoryCount > 1 && (
        <Grid item xs={12} sm={3} mx={2} sx={{ height: { sm: "30px" }, mt: { xs: 2, sm: -1 } }}>
          <Grid container direction="column">
            <Grid item xs={12}>
              <Typography id={`slider-label-${index}`}>{t("shop.inventory.inventoryQuantity")}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Slider
                value={inventory.quantity}
                onChange={(e, value) => {
                  if (inventory.quantity !== value) updateQuantity(value as number);
                }}
                disabled={inventoryCount >= 3}
                step={null}
                valueLabelDisplay="auto"
                aria-labelledby={`slider-label-${index}`}
                valueLabelFormat={valueLabelFormat}
                marks={SLIDER_MARKS}
                min={25}
                max={75}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default CellInventoryForm;
